<template>
  <v-card class="ma-1">
    <v-toolbar>
      <v-btn
        v-b-tooltip
        class="mr-3"
        title="Back to Alerts Home"
        @click="$router.push('/alerts')"
        aria-label="Back to Alerts Home"
      >
        <v-icon>mdi-arrow-up</v-icon>
      </v-btn>

      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        hide-details
        label="Search in any field"
        single-line
        style="max-width: 15em"
      />

      <v-spacer />

      <v-btn-toggle dense>
        <FileUploadDialog
          :upload-url="`${ALERTS_URL}/groups`"
          button-title="mdi-file-upload-outline"
          label="JSON file"
          title="Create from JSON file"
          @uploaded="onFileUploaded"
        />

        <PromptDialog
          v-model="visibleCopy"
          :text="editingItem ? editingItem.value : null"
          :visible="false"
          title="Do you want to copy?"
          @confirmed="copyItem"
        />

        <DeleteDialog
          v-model="visibleDelete"
          :api-url="ALERTS_URL"
          :items="selected"
          :visible="false"
          button-title="mdi-delete"
          object-type="groups"
          title="Delete selected actions groups"
          @click="deleteItems(selected)"
          @itemDeleted="removeItem"
        />

        <CheckEditor
          v-model="visibleEdit"
          :item="editingItem"
          :on-button-activate="resetEditingItem"
          button-title="mdi-playlist-plus"
          global
          max-width="600"
          title="Add"
          @onSave="itemUpdated"
        />
      </v-btn-toggle>
    </v-toolbar>

    <v-data-table
      :footer-props="{
        showFirstLastPage: true,
        itemsPerPageOptions: [10, 30, 50, -1],
      }"
      :headers="headers"
      :hide-default-footer="items.length <= 10"
      :items="items"
      :items-per-page="30"
      :search="search"
      item-key="key"
    >
      <template #[`item.type`]="{ item }">{{ item['@c'].substring(1) }}</template>

      <template #[`item.logicType`]="{ item }">
        <span
          v-if="item.logicType === 'OR'"
          v-b-tooltip
          title="ANY (at least one condition must be met)"
        >
          <v-icon color="green">mdi-gate-or</v-icon>
        </span>
        <span
          v-else-if="item.logicType === 'AND'"
          v-b-tooltip
          title="ALL (all conditions must be met)"
        >
          <v-icon color="blue">mdi-gate-and</v-icon>
        </span>
      </template>

      <template #[`item.name`]="{ item }">
        <b-link :to="`/alerts/groups/${encodeURIComponent(item.name)}`">
          {{ item.text || item.name }}
        </b-link>
      </template>

      <template #[`item.recordsNumber`]="{ item }">
        <v-chip small>{{ item.recordsNumber }}</v-chip>
      </template>

      <template #[`item.warning`]="{ item }">
        <v-icon
          v-if="item.warning"
          v-b-tooltip
          :title="item.warning"
          color="red"
        >
          mdi-message-alert-outline
        </v-icon>
      </template>

      <template #[`item.frozen`]="{ item }">
        <v-icon
          v-if="item.frozen"
          style="color: deepskyblue"
          title="Frozen"
        >
          mdi-snowflake
        </v-icon>
      </template>

      <template #[`item.actions`]="{ item }">
        <v-icon
          v-if="!item.frozen"
          class="ma-1"
          title="Edit"
          @click="editItem(item)"
        >
          mdi-playlist-edit
        </v-icon>
        <v-icon
          class="ma-1"
          title="Copy"
          @click="confirmCopyItem(item)"
        >
          mdi-content-copy
        </v-icon>
        <v-icon
          v-if="!item.frozen"
          class="ma-1"
          title="Delete"
          @click="deleteItems([item])"
        >
          mdi-delete
        </v-icon>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { ALERTS_URL } from '@/main'
import DeleteDialog from '@/components/dialogs/DeleteDialog.vue'
import PromptDialog from '@/components/dialogs/PromptDialog.vue'
import CheckEditor from '@/components/alerts/CheckEditor'
import FileUploadDialog from '@/components/dialogs/FileUploadDialog.vue'
import ChecksGroupBase from '@/components/alerts/ChecksGroupBase'

export default {
  mixins: [ChecksGroupBase],
  components: { PromptDialog, DeleteDialog, CheckEditor, FileUploadDialog },

  data: () => ({
    headers: [
      { text: 'Type', value: 'type' },
      { text: 'Logic', value: 'logicType' },
      { text: 'Name', value: 'name' },
      { text: 'Type', value: 'groupType' },
      { text: 'Category', value: 'category' },
      { text: 'Description', value: 'description' },
      { text: 'Checks #', value: 'recordsNumber', filterable: false },
      { text: 'Warning', value: 'warning', filterable: false, width: '1%' },
      { text: 'Frozen', value: 'frozen', filterable: false, width: '1%' },
      {
        text: 'Actions',
        value: 'actions',
        sortable: false,
        filterable: false,
        align: 'end',
      },
    ],
  }),

  methods: {
    removeItem(item) {
      this.items.splice(this.items.indexOf(item), 1)
    },
    copyItem() {
      this.$axios.post(`${ALERTS_URL}/groups`, this.editingItem).then((response) => {
        response.data.key = this.maxKey++
        response.data.recordsNumber = response.data.operations ? response.data.operations.length : 0
        this.items.push(response.data)
      })
    },
    updateDatabase(group) {
      if (this.editingItem)
        this.$axios.patch(`${ALERTS_URL}/groups/${this.editingItem.id}`, this.editingItem).then(() => {
          //
          // this.$router.go(0);
        })
      else
        this.$axios.post(`${ALERTS_URL}/groups`, group).then(() => {
          //
        })
    },
    onFileUploaded(group) {
      group.key = this.maxKey++
      group.recordsNumber = group.operations ? group.operations.length : 0
      this.items.push(group)
    },
    getData() {
      this.maxKey = 0
      this.$axios.get(`${ALERTS_URL}/groups`).then((response) => {
        response.data.forEach((s) => {
          s.recordsNumber = s.operations ? s.operations.length : 0
          s.key = this.maxKey++

          if (s.recordsNumber) {
            if (s.logicType === 'AND') {
              const nonSmiles = s.operations.find((o) => o.type !== 'SMILES')
              if (!nonSmiles) s.warning = 'ALL group consists of EXACT checks only - results is always FALSE'
            }

            const types = s.operations.map((o) => o.type).filter((v, i, a) => a.indexOf(v) === i)
            if (types.length > 1) s.groupType = 'MIXED'
            else if (types[0] === 'SMARTS') s.groupType = 'QUERY'
            else if (types[0] === 'SMILES') s.groupType = 'LOOKUP'
            else s.groupType = types[0]
          }
        })
        this.items = response.data
      })
    },
  },
}
</script>
